import React, { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import SetAlertButton from './SetAlertButton';
import { createEmailAlert, getSavedSearches } from '../../searchResult/model/listingBffRepository';
import { getQueryFromUrlString } from '../../searchResult/model/query';
import { parseConfig } from '../../../shared/config';
import { canSetAlert } from '../../../shared/canSetAlert';

type FetchArgs = {
  isSignedIn: boolean;
  supportedAlert: boolean;
  url: string;
  myRcaToken?: string;
  setIsLoading: (isLoading: boolean) => void;
  setIsAlertSet: (isAlertSet: boolean) => void;
};

type SetAlertContainerProps = {
  url: string;
  myRcaToken: string | undefined;
  openOverlay: () => void;
  fetcher?: (args: FetchArgs) => Promise<void>;
};

const openEditAlert = (): void => {
  const { WEBSITE_ROOT } = parseConfig() as Record<string, string>;

  window.open(`${WEBSITE_ROOT}/my-commercial/saved-searches-and-email-alerts`, '_blank');
};

const addAlert = (url: string, myRcaToken: string) => {
  createEmailAlert(getQueryFromUrlString(url), myRcaToken);
};

const fetchAlert = async ({
  isSignedIn,
  supportedAlert,
  url,
  myRcaToken,
  setIsLoading,
  setIsAlertSet,
}: FetchArgs) => {
  if (isSignedIn && supportedAlert) {
    const { alertSet } = await getSavedSearches(getQueryFromUrlString(url), myRcaToken);

    setIsLoading(false);
    setIsAlertSet(alertSet);

    return;
  }

  setIsLoading(false);
};

const SetAlertContainer = ({
  url,
  myRcaToken,
  openOverlay,
  fetcher = fetchAlert,
}: SetAlertContainerProps): ReactElement | null => {
  const [isAlertSet, setIsAlertSet] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isSignedIn = !!myRcaToken;
  const supportedAlert = canSetAlert(url);
  const shouldDisableButton = (): boolean => {
    if (isLoading) return true;
    if (!isSignedIn) return false;
    if (!supportedAlert) return true;

    return false;
  };

  const handleClick = (): void => {
    if (!isSignedIn) {
      return openOverlay();
    }

    if (isAlertSet) {
      return openEditAlert();
    }

    addAlert(url, myRcaToken);

    return setIsAlertSet(true);
  };

  useEffect(() => {
    fetcher({ isSignedIn, supportedAlert, url, myRcaToken, setIsLoading, setIsAlertSet });
  }, [url]);

  return (
    <SetAlertButton disabled={shouldDisableButton()} onClick={handleClick} alertSet={isAlertSet} />
  );
};

export default SetAlertContainer;
